<template>
  <core-modal-marketing
    :tag="content.modalEyebrow"
    tag-icon="i-ri-dna-line"
    :title="content.modalTitle"
    :subtitle="content.modalDescription"
    header-icon="i-ri-dna-line"
  >
    <div class="mx-auto max-w-6xl">
      <ClientOnly>
        <template v-if="pending">
          <div class="mx-auto h-12 w-full max-w-lg animate-pulse rounded-full bg-slate-100 ring-1 ring-slate-200 sm:h-14" />
          <div class="mt-6 grid gap-4 lg:grid-cols-3">
            <core-upsell-product-card-skeleton
              v-for="index in 3"
              :key="`plan-${index}`"
            />
          </div>
          <div class="mt-6 h-28 animate-pulse rounded-2xl bg-slate-100 ring-1 ring-slate-200" />
        </template>

        <core-contextual-error-box
          v-else-if="fetchError"
          size="sm"
          class="my-3 rounded-md border-2 border-gray-200"
          :action="retry"
        >
          <template #heading>Unable to Load Genealogy Plans</template>
          <template #default>Please try again or contact support.</template>
        </core-contextual-error-box>

        <template v-else>
          <section>
            <p class="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Choose your project type
            </p>

            <u-tabs :items="projectTypes" :default-index="0" class="mt-2">
              <template #item="{ item }">
                <section>
                  <p class="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Pick your project length
                  </p>

                  <div class="mt-2 grid gap-4 lg:grid-cols-3">
                    <article
                      v-for="product in getProductsForType(item.value)"
                      :key="product.id"
                      class="relative flex h-full flex-col overflow-hidden rounded-xl border bg-white p-5 text-center transition-shadow hover:shadow sm:p-6"
                      :class="product.months === 12 ? item.featuredClass : 'border-charcoal-200'"
                    >
                      <span
                        v-if="product.months === 12"
                        class="absolute left-0 top-0 rounded-br-xl px-4 py-1 text-xs font-bold uppercase text-white"
                        :class="item.badgeClass"
                      >
                        Best Value
                      </span>

                      <span class="block text-xl font-semibold text-slate-900">
                        {{ product.months }} Months
                      </span>

                      <span class="mt-4 block">
                        <core-price-display
                          class="text-3xl"
                          :base-price="product.price_cents"
                          :discounted-price="product.promotionPrice"
                          :precision="product.pricePrecision"
                          :precision-slashed="product.basePricePrecision"
                          :show-suffix="false"
                          smaller-discount
                        />
                        <span class="text-base font-medium text-slate-500">/month</span>
                      </span>

                      <span class="mt-4 flex items-center justify-center gap-3 text-sm text-slate-600">
                        <span>{{ product.totalPrice }} total</span>
                        <span class="h-5 w-px bg-slate-200" />
                        <span>{{ product.hourlyRate }}/hour</span>
                      </span>

                      <u-button
                        :color="item.buttonColor"
                        size="lg"
                        block
                        class="mt-3 justify-center"
                        :class="item.buttonClass"
                        label="Add to Cart"
                        :loading="addingProductId === product.id"
                        :disabled="Boolean(addingProductId)"
                        @click="addToCart(product)"
                      />
                    </article>
                  </div>

                  <u-alert
                    class="mx-auto mt-5 w-fit max-w-full"
                    color="white"
                    variant="solid"
                    icon="i-ri-price-tag-3-line"
                    :title="item.includedHoursText"
                    :ui="{
                      rounded: 'rounded-2xl',
                      padding: 'px-5 py-4',
                      gap: 'gap-4 justify-center',
                      inner: 'w-auto flex-none',
                      icon: {base: 'h-7 w-7 shrink-0 text-heroblue-600'},
                      title: 'text-center text-sm font-semibold leading-6 text-slate-700 sm:whitespace-nowrap sm:text-base'
                    }"
                  />
                </section>
              </template>
            </u-tabs>
          </section>

          <div class="my-5 flex items-center gap-4" aria-hidden="true">
            <div class="h-px flex-1 bg-slate-200" />
            <span class="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-600 shadow-sm">
              OR
            </span>
            <div class="h-px flex-1 bg-slate-200" />
          </div>

          <section class="rounded-2xl border border-shell-700 bg-shell-100 p-5">
            <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
              <div class="flex min-w-0 flex-1 items-center gap-4">
                <span class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-shell-800">
                  <u-icon name="i-ri-calendar-event-line" class="h-7 w-7 text-white" />
                </span>
                <div class="min-w-0">
                  <h2 class="text-xl font-semibold text-slate-900">
                    Not sure which is right for you?
                  </h2>
                  <p class="mt-1 text-sm leading-6 text-slate-600">
                    Book a one-time consultation with an expert.
                  </p>
                </div>
              </div>

              <div class="shrink-0 border-shell-700 sm:border-l sm:pl-6 sm:text-right">
                <core-price-display
                  class="text-2xl"
                  :base-price="consultationProduct.price_cents"
                  :discounted-price="consultationProduct.promotionPrice"
                  :precision="consultationProduct.pricePrecision"
                  :precision-slashed="consultationProduct.basePricePrecision"
                  :show-suffix="false"
                  smaller-discount
                />&nbsp;
                <span class="text-sm font-medium text-slate-500">one time</span>
              </div>

              <u-button
                color="neutral"
                variant="outline"
                size="lg"
                class="shrink-0 justify-center border-shell-800 text-shell-900 hover:bg-shell-200"
                label="Book Consultation"
                :loading="addingProductId === consultationProduct.id"
                :disabled="Boolean(addingProductId)"
                @click="addToCart(consultationProduct)"
              />
            </div>
          </section>

          <p class="mt-8 text-center">
            <u-link
              :to="content.ctaTo"
              external
              class="text-sm text-gray-500 underline"
            >
              Learn More About FOREVER Genealogy
            </u-link>
          </p>
        </template>
      </ClientOnly>
    </div>
  </core-modal-marketing>
</template>

<script setup>
  import {EXTERNAL_URLS} from '~/constants/external-urls.js';

  const props = defineProps({
    upsellTracking: Object
  });

  const GENEALOGY_PRODUCT_GROUP = 'forever-family-research';
  const PRODUCT_DEFINITIONS = [
    {
      slug: 'family-research-12-month-plan',
      type: 'research',
      months: 12,
      hoursPerMonth: 5
    },
    {
      slug: 'family-research-6-month-plan',
      type: 'research',
      months: 6,
      hoursPerMonth: 5
    },
    {
      slug: 'family-research-3-month-plan',
      type: 'research',
      months: 3,
      hoursPerMonth: 5
    },
    {
      slug: 'genealogy-coaching-project-12-month-plan',
      type: 'coaching',
      months: 12,
      hoursPerMonth: 1
    },
    {
      slug: 'genealogy-coaching-project-6-month-plan',
      type: 'coaching',
      months: 6,
      hoursPerMonth: 1
    },
    {
      slug: 'genealogy-coaching-project-3-month-plan',
      type: 'coaching',
      months: 3,
      hoursPerMonth: 1
    },
    {
      slug: 'family-research-consultation',
      type: 'consultation'
    }
  ];

  const content = {
    modalEyebrow: 'Genealogy Research',
    modalTitle: 'Discover the stories behind your family tree.',
    modalDescription: 'FOREVER Genealogy offers expert-led research and coaching to help you uncover, organize, and preserve your family history.',
    ctaTo: EXTERNAL_URLS.familyResearch
  };

  const projectTypes = [
    {
      value: 'research',
      label: 'Research Project',
      includedHoursText: 'Monthly price includes 5 research hours each month.',
      featuredClass: 'border-2 border-heroblue-600 bg-ice-50',
      badgeClass: 'bg-heroblue-600',
      buttonColor: 'heroblue'
    },
    {
      value: 'coaching',
      label: 'Coaching Project',
      includedHoursText: 'Monthly price includes 1 coaching hour each month.',
      featuredClass: 'border-2 border-sage-900 bg-sage-50',
      badgeClass: 'bg-sage-900',
      buttonColor: 'sage',
      buttonClass: 'bg-sage-900 text-white hover:bg-sage-950'
    }
  ];

  const {
    products,
    pending,
    error,
    retry
  } = usePricingGroup({
    groupSlug: GENEALOGY_PRODUCT_GROUP,
    includeDescriptions: true,
    server: false
  });

  function getPrecision(price) {
    return Number(price) % 100 === 0 ? 0 : 2;
  }

  function getFormattedPrice(price) {
    return formatCurrency(price, {
      precision: getPrecision(price),
      showSuffix: false
    });
  }

  const mappedProducts = computed(() => PRODUCT_DEFINITIONS.map(definition => {
    const product = products.value?.find(item => item.slug === definition.slug);

    if (!product) {
      return null;
    }

    const effectivePrice = product.promotionPrice ?? product.price_cents;

    return {
      ...product,
      ...definition,
      pricePrecision: getPrecision(effectivePrice),
      basePricePrecision: getPrecision(product.price_cents),
      totalPrice: definition.months
        ? getFormattedPrice(effectivePrice * definition.months)
        : null,
      hourlyRate: definition.hoursPerMonth
        ? getFormattedPrice(effectivePrice / definition.hoursPerMonth)
        : null
    };
  }));

  const hasRequiredProducts = computed(() => mappedProducts.value.every(Boolean));
  const fetchError = computed(() =>
    !pending.value &&
    (
      Boolean(error.value) ||
      !hasRequiredProducts.value
    )
  );

  function getProductsForType(type) {
    return hasRequiredProducts.value
      ? mappedProducts.value
          .filter(product => product.type === type)
          .sort((a, b) => a.months - b.months)
      : [];
  }
  const consultationProduct = computed(() =>
    hasRequiredProducts.value
      ? mappedProducts.value.find(product => product.type === 'consultation')
      : null
  );

  const addingProductId = ref(null);

  async function addToCart(product) {
    if (addingProductId.value || !product) {
      return;
    }

    addingProductId.value = product.id;

    try {
      trackUpsellCta({
        tracking: props.upsellTracking,
        product
      });

      await usePricingApi().addToCart({
        productId: product.id,
        promotionCode: product.promotionCode
      });

      window.location = EXTERNAL_URLS.cart;
    } catch {
      useErrorToast().add('Unable to add to cart at this time. Please try again later.');
    } finally {
      addingProductId.value = null;
    }
  }
</script>
